import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { GroupPickerField } from '@lumapps/group-pickers/components/GroupPickerField';
import { UseGroupPickerFieldStateOptions } from '@lumapps/group-pickers/hooks/useGroupPickerFieldState';
import { Group } from '@lumapps/groups/types';
import { doesCurrentInstanceAllowPublicContent } from '@lumapps/instance/ducks/selectors';
import { mdiAccountPlus } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { SpaceSelect } from '@lumapps/spaces/components/SpaceSelect';
import { isContentInSpaceFeatureEnabled } from '@lumapps/spaces/ducks/selectors';
import { useTranslate } from '@lumapps/translations';

import { CONTENTS } from '../../keys';

import './index.scss';

export type ContentAudienceTargetingProps = {
    defaultVisibleByIds: string[];
    defaultHighlightedForIds: string[];
    /** IDs of the spaces the content is visible in. Only used when the `content-in-space` FF is enabled. */
    defaultCommunityIds?: string[];
    restrictToFeedsIds?: string[];
    isVisibleByMissing?: boolean;
    isDisabled?: boolean;
    onVisibleChange: (visibleByGroups: (string | Group)[]) => void;
    onHighlightedChange: (highlightedForGroups: (string | Group)[]) => void;
    /** Callback with the selected space ids. Only used when the `content-in-space` FF is enabled. */
    onCommunityChange?: (communityIds: string[]) => void;
    groupAll?: Group;
    groupPublic?: Group;
};

const CLASSNAME = 'content-audience-targeting';

export const ContentAudienceTargeting = ({
    defaultVisibleByIds,
    defaultHighlightedForIds,
    defaultCommunityIds,
    restrictToFeedsIds,
    groupAll,
    groupPublic,
    isVisibleByMissing,
    isDisabled = false,
    onVisibleChange,
    onHighlightedChange,
    onCommunityChange,
}: ContentAudienceTargetingProps) => {
    const { translateKey } = useTranslate();
    const isPublicContentAuthorized = useSelector(doesCurrentInstanceAllowPublicContent);
    const isContentInSpaceEnabled = useSelector(isContentInSpaceFeatureEnabled);
    const { block, element } = useClassnames(CLASSNAME);

    // Need internal state to have great perf in legacy context when the user pick a group
    const [visibleBy, setVisibleBy] = React.useState<(Group | string)[]>(defaultVisibleByIds ?? []);
    const [highlightedFor, setHighlightedFor] = React.useState<(Group | string)[]>(defaultHighlightedForIds ?? []);
    const [communityIds, setCommunityIds] = React.useState<string[]>(defaultCommunityIds ?? []);

    const containsAllOrPublic = React.useCallback(
        (ids: string[]) => (groupAll && ids.includes(groupAll?.id)) || (groupPublic && ids.includes(groupPublic.id)),
        [groupAll, groupPublic],
    );

    const isAllInRestriction = React.useMemo(() => {
        if (groupAll && restrictToFeedsIds) {
            return restrictToFeedsIds.includes(groupAll.id);
        }

        return false;
    }, [restrictToFeedsIds, groupAll]);

    const isPublicInRestriction = React.useMemo(() => {
        if (groupPublic && restrictToFeedsIds) {
            return restrictToFeedsIds.includes(groupPublic.id);
        }

        return false;
    }, [restrictToFeedsIds, groupPublic]);

    /** Build custom params */
    const customVisibleByParams = React.useMemo<UseGroupPickerFieldStateOptions['searchParams']>(
        () => ({
            restrictToFeeds: isAllInRestriction || isPublicInRestriction ? [] : restrictToFeedsIds,
            excludeSegments: false,
        }),
        [restrictToFeedsIds, isAllInRestriction, isPublicInRestriction],
    );
    const customHighlightedForParams = React.useMemo<UseGroupPickerFieldStateOptions['searchParams']>(() => {
        const visibleByIds = visibleBy.map((gr) => (typeof gr === 'string' ? gr : gr.id));
        return {
            restrictToFeeds: containsAllOrPublic(visibleByIds) ? [] : visibleByIds,
            excludeSegments: false,
        };
    }, [visibleBy, containsAllOrPublic]);

    const shouldDisplayDefaultGroups = React.useMemo(() => {
        const ids = visibleBy.map((gr) => (typeof gr === 'string' ? gr : gr.id));
        return groupAll && ids.includes(groupAll?.id);
    }, [groupAll, visibleBy]);

    const onHighlightedForChange = React.useCallback(
        (groups: (string | Group)[] | undefined) => {
            setHighlightedFor(groups ?? []);
            onHighlightedChange(groups ?? []);
        },
        [onHighlightedChange],
    );

    const onCommunitiesChange = React.useCallback(
        (ids: string[]) => {
            setCommunityIds(ids);
            onCommunityChange?.(ids);
        },
        [onCommunityChange],
    );

    const onVisibleByChange = React.useCallback(
        (groups: Group[] | undefined) => {
            const groupIds = groups?.map((gr) => gr.id);

            setVisibleBy(groups ?? []);
            onVisibleChange(groups ?? []);

            // Can only highlight for groups that are in the visible by
            if (groupIds && !containsAllOrPublic(groupIds)) {
                // check if some highlighted for groups should be removed
                const cleanedGroups = highlightedFor.filter((group) =>
                    groupIds.includes(typeof group === 'string' ? group : group.id),
                );

                onHighlightedForChange(cleanedGroups);
            }
        },
        [containsAllOrPublic, highlightedFor, onHighlightedForChange, onVisibleChange],
    );

    // When spaces are part of the visible-by section, either a group OR a space satisfies the
    // requirement, so the audience is only missing when both are empty.
    const isAudienceMissing = Boolean(isVisibleByMissing) && visibleBy.length === 0 && communityIds.length === 0;

    // With spaces enabled the group picker only labels its own "Group" sub-field; otherwise it carries
    // the whole "Visible by" label.
    const visibleByGroupLabel = translateKey(
        isContentInSpaceEnabled ? CONTENTS.SETTINGS_VISIBLE_BY_GROUP : CONTENTS.SETTINGS_VISIBLE_BY,
    );
    const visibleByGroupHelper = translateKey(
        isContentInSpaceEnabled ? CONTENTS.SETTINGS_VISIBLE_BY_GROUP_HELPER : CONTENTS.SETTINGS_VISIBLE_BY_HELP,
    );
    // When spaces are enabled, either a group or a space satisfies the requirement, so the same
    // "group or space" message is shown on each field.
    const audienceErrorMessage = translateKey(
        isContentInSpaceEnabled ? CONTENTS.SETTINGS_VISIBLE_BY_ERROR : CONTENTS.FEED_REQUIRED,
    );

    const shouldDisplayPublicGroup =
        isPublicContentAuthorized &&
        (!restrictToFeedsIds || (groupPublic && (isPublicInRestriction || isAllInRestriction)));
    const shouldDisplayAllGroup = !restrictToFeedsIds || (groupAll && (isAllInRestriction || isPublicInRestriction));

    const visibleByPicker = (
        <GroupPickerField
            selectionType="multiple"
            label={visibleByGroupLabel}
            onChange={onVisibleByChange}
            value={visibleBy}
            searchParams={customVisibleByParams}
            shouldDisplayPublicGroup={shouldDisplayPublicGroup}
            shouldDisplayAllGroup={shouldDisplayAllGroup}
            className={element('visible-by')}
            selectTextFieldProps={{
                helper: visibleByGroupHelper,
                isDisabled,
                icon: mdiAccountPlus,
                scope: `${CLASSNAME}-visible-by`,
                error: audienceErrorMessage,
                hasError: isAudienceMissing,
            }}
        />
    );

    return (
        <div className={block()}>
            {isContentInSpaceEnabled ? (
                <div className={element('visible-by-section')}>
                    {visibleByPicker}
                    <SpaceSelect
                        selectionType="multiple"
                        label={translateKey(CONTENTS.SETTINGS_VISIBLE_BY_SPACE)}
                        value={communityIds}
                        onChange={onCommunitiesChange}
                        isDisabled={isDisabled}
                        className={element('space-select')}
                        scope={`${CLASSNAME}-spaces`}
                        helper={translateKey(CONTENTS.SETTINGS_VISIBLE_BY_SPACE_HELPER)}
                        hasError={isAudienceMissing}
                        error={isAudienceMissing ? audienceErrorMessage : undefined}
                    />
                </div>
            ) : (
                visibleByPicker
            )}
            <GroupPickerField
                selectionType="multiple"
                label={translateKey(CONTENTS.SETTINGS_HIGHLIGHTED_FEEDS)}
                onChange={onHighlightedForChange}
                value={highlightedFor}
                searchParams={customHighlightedForParams}
                shouldDisplayPublicGroup={false}
                shouldDisplayAllGroup={shouldDisplayDefaultGroups}
                shouldDisplaySegments
                className={element('highlighted-for')}
                selectTextFieldProps={{
                    helper: translateKey(CONTENTS.SETTINGS_HIGHLIGHTED_FEEDS_HELP),
                    scope: `${CLASSNAME}-highlighted-for`,
                    icon: mdiAccountPlus,
                    isDisabled: visibleBy.length === 0 || isDisabled,
                }}
            />
        </div>
    );
};
